/**
 * Opportunity Detail Drawer Component
 * Full opportunity details in a right-side drawer with tabs
 */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Badge, Button, ScrollArea, Separator, Sheet, SheetContent, SheetHeader, SheetTitle, Tabs, TabsContent, TabsList, TabsTrigger } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import DOMPurify from 'dompurify';
import { Building2, Calendar, Clock, ExternalLink, FileText, GitBranch, Sparkles, Tag } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCanonicalOpportunities, useNoticeCanonicalLinks } from '../hooks/useCanonicalOpportunities';
import { useOpportunityHistory } from '../hooks/useOpportunityHistory';
import { useQualificationEvaluation, useQualificationMutations, useQualificationVersions } from '../hooks/useQualification';
import { useSimilarOpportunities } from '../hooks/useSimilarOpportunities';
import { OpportunityWatch } from '../Watch/OpportunityWatch';
import { LinkedOpportunities } from './LinkedOpportunities';
import { NoticeCoverage } from './NoticeCoverage';
import { NoticePublications } from './NoticePublications';
import { QualificationSection } from './QualificationSection';
/** Deliberate entry: opening existing work never changes its lifecycle. */ export function OpportunityQualificationEntry({ opportunityId, title }) {
    const [, setParams] = useSearchParams();
    const evaluation = useQualificationEvaluation(opportunityId);
    const versions = useQualificationVersions();
    const { start } = useQualificationMutations(opportunityId);
    const open = ()=>setParams((previous)=>{
            const next = new URLSearchParams(previous);
            next.set('qualification', opportunityId);
            return next;
        });
    const failed = evaluation.error || !evaluation.data && versions.error;
    const loading = evaluation.isLoading || !evaluation.data && versions.isLoading;
    const missingTemplate = !evaluation.data && !loading && !failed && !versions.data?.length;
    return /*#__PURE__*/ _jsxs("section", {
        "data-testid": Testids.qualification.entry,
        "data-opportunity-id": opportunityId,
        className: "space-y-2 text-sm",
        children: [
            /*#__PURE__*/ _jsx("p", {
                className: "font-medium",
                children: title
            }),
            loading && /*#__PURE__*/ _jsx("output", {
                children: "Loading Qualification…"
            }),
            failed ? /*#__PURE__*/ _jsxs("div", {
                role: "alert",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        children: "Qualification unavailable. Check your access or try again."
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        size: "sm",
                        onClick: ()=>{
                            void evaluation.refetch();
                            void versions.refetch();
                        },
                        children: "Retry"
                    })
                ]
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    missingTemplate && /*#__PURE__*/ _jsx("p", {
                        children: "No Qualification template is published. Ask a Growth administrator to publish one."
                    }),
                    start.isError && /*#__PURE__*/ _jsx("p", {
                        role: "alert",
                        children: "Could not start Qualification. Try again."
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        size: "sm",
                        "data-testid": Testids.qualification.entryButton,
                        disabled: loading || missingTemplate || start.isPending,
                        onClick: ()=>{
                            if (evaluation.data) open();
                            else start.mutate(undefined, {
                                onSuccess: open
                            });
                        },
                        children: start.isPending ? 'Starting Qualification…' : evaluation.data ? 'Resume Qualification' : 'Start Qualification'
                    })
                ]
            })
        ]
    });
}
function NoticeQualificationEntries({ noticeId }) {
    const { linked } = useNoticeCanonicalLinks(noticeId);
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: linked.map((link)=>/*#__PURE__*/ _jsx(OpportunityQualificationEntry, {
                opportunityId: link.opportunityId,
                title: link.title
            }, link.opportunityId))
    });
}
/** URL-backed workspace reuses QUAL-01 and leaves the market desk URL intact. */ export function OpportunityQualificationWorkspace({ opportunityId }) {
    const [params, setParams] = useSearchParams();
    const opportunities = useCanonicalOpportunities();
    const opportunity = opportunities.data?.find((o)=>o.id === opportunityId);
    const back = ()=>{
        // The URL retains the originating desk, including for shared links.
        const next = new URLSearchParams(params);
        next.delete('qualification');
        setParams(next, {
            replace: true
        });
    };
    return /*#__PURE__*/ _jsxs("section", {
        "data-testid": Testids.qualification.workspace,
        "data-opportunity-id": opportunityId,
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx(Button, {
                variant: "outline",
                "data-testid": Testids.qualification.back,
                onClick: back,
                children: "Back to Opportunities"
            }),
            opportunities.isLoading ? /*#__PURE__*/ _jsx("output", {
                children: "Loading Opportunity…"
            }) : opportunity ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs("h1", {
                        className: "text-xl font-semibold",
                        children: [
                            "Qualification — ",
                            opportunity.title
                        ]
                    }),
                    /*#__PURE__*/ _jsx(QualificationSection, {
                        opportunityId: opportunity.id,
                        pursuitName: opportunity.title
                    })
                ]
            }) : /*#__PURE__*/ _jsxs("div", {
                role: "alert",
                children: [
                    /*#__PURE__*/ _jsx("h1", {
                        children: "Qualification unavailable"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        children: "This Opportunity is unavailable or you do not have access."
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        onClick: ()=>{
                            void opportunities.refetch();
                        },
                        children: "Retry"
                    })
                ]
            })
        ]
    });
}
const SANITIZE_CONFIG = {
    ALLOWED_TAGS: [
        'p',
        'br',
        'a',
        'strong',
        'em',
        'ul',
        'ol',
        'li'
    ],
    ALLOWED_ATTR: [
        'href',
        'target',
        'rel'
    ],
    KEEP_CONTENT: true,
    FORCE_BODY: true
};
function formatDate(dateString) {
    try {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        });
    } catch  {
        return dateString;
    }
}
function getDaysUntilDeadline(deadline) {
    if (!deadline) return 'Not specified';
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays < 0) {
        return `Expired ${Math.abs(diffDays)} days ago`;
    }
    if (diffDays === 0) {
        return 'Due today';
    }
    if (diffDays === 1) {
        return '1 day remaining';
    }
    return `${diffDays} days remaining`;
}
export function OpportunityDetailModal({ opportunity, open, onOpenChange, onNavigate }) {
    // Track which tab is active to lazy-load Similar/History only when needed
    const [activeTab, setActiveTab] = useState('details');
    const handleViewOnSAM = ()=>{
        if (opportunity.uiLink) {
            window.open(opportunity.uiLink, '_blank', 'noopener,noreferrer');
        }
    };
    const daysUntil = getDaysUntilDeadline(opportunity.responseDeadLine);
    const daysUntilNum = opportunity.responseDeadLine ? Math.ceil((new Date(opportunity.responseDeadLine).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null;
    const isUrgent = daysUntilNum !== null && daysUntilNum <= 7 && daysUntilNum > 0;
    const sanitizedDescription = DOMPurify.sanitize(opportunity.description, SANITIZE_CONFIG);
    // Lazy-load Similar/History tabs - only fetch when tab is activated
    const { data: similarData, isLoading: similarLoading, error: similarError } = useSimilarOpportunities(opportunity.noticeId, activeTab === 'similar');
    const { data: historyData, isLoading: historyLoading, error: historyError } = useOpportunityHistory(opportunity.noticeId, activeTab === 'history');
    return /*#__PURE__*/ _jsx(Sheet, {
        open: open,
        onOpenChange: onOpenChange,
        children: /*#__PURE__*/ _jsxs(SheetContent, {
            side: "right",
            className: "w-[680px] max-w-full min-w-[360px] p-0 flex flex-col",
            "data-testid": Testids.opportunities.detailPanel,
            children: [
                /*#__PURE__*/ _jsxs(SheetHeader, {
                    className: "px-6 pt-6 pb-4 border-b shrink-0",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex gap-2 mb-2 flex-wrap pr-6",
                            children: [
                                opportunity.typeOfSetAsideDescription && /*#__PURE__*/ _jsx(Badge, {
                                    variant: "outline",
                                    children: opportunity.typeOfSetAsideDescription
                                }),
                                isUrgent && /*#__PURE__*/ _jsx(Badge, {
                                    variant: "destructive",
                                    children: "Urgent"
                                }),
                                opportunity.hasAttachments && /*#__PURE__*/ _jsxs(Badge, {
                                    variant: "secondary",
                                    children: [
                                        /*#__PURE__*/ _jsx(FileText, {
                                            className: "h-3 w-3 mr-1"
                                        }),
                                        opportunity.attachmentCount,
                                        " Attachment",
                                        opportunity.attachmentCount !== 1 ? 's' : ''
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SheetTitle, {
                            className: "text-xl leading-snug pr-6",
                            children: opportunity.title
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "flex-1 overflow-y-auto overflow-x-auto min-w-0",
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "min-w-[320px]",
                        children: /*#__PURE__*/ _jsxs(Tabs, {
                            defaultValue: "details",
                            value: activeTab,
                            onValueChange: setActiveTab,
                            className: "flex flex-col h-full",
                            children: [
                                /*#__PURE__*/ _jsxs(TabsList, {
                                    className: "w-full rounded-none border-b bg-transparent px-6 justify-start gap-6 h-auto py-0 shrink-0",
                                    children: [
                                        /*#__PURE__*/ _jsx(TabsTrigger, {
                                            value: "details",
                                            className: "rounded-none border-b-2 border-transparent px-0 pb-3 pt-3 font-medium data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:text-muted-foreground",
                                            children: "Details"
                                        }),
                                        /*#__PURE__*/ _jsx(TabsTrigger, {
                                            value: "description",
                                            className: "rounded-none border-b-2 border-transparent px-0 pb-3 pt-3 font-medium data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:text-muted-foreground",
                                            children: "Description"
                                        }),
                                        /*#__PURE__*/ _jsxs(TabsTrigger, {
                                            value: "similar",
                                            className: "rounded-none border-b-2 border-transparent px-0 pb-3 pt-3 font-medium data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:text-muted-foreground flex items-center gap-1.5",
                                            children: [
                                                "Similar",
                                                similarData && similarData.total > 0 && /*#__PURE__*/ _jsx("span", {
                                                    className: "inline-flex items-center justify-center h-4 min-w-4 px-1 text-[10px] font-bold leading-none rounded-full bg-primary text-primary-foreground",
                                                    children: similarData.total
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs(TabsTrigger, {
                                            value: "history",
                                            className: "rounded-none border-b-2 border-transparent px-0 pb-3 pt-3 font-medium data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:text-muted-foreground flex items-center gap-1.5",
                                            children: [
                                                "History",
                                                historyData && historyData.total > 1 && /*#__PURE__*/ _jsx("span", {
                                                    className: "inline-flex items-center justify-center h-4 min-w-4 px-1 text-[10px] font-bold leading-none rounded-full bg-primary text-primary-foreground",
                                                    children: historyData.total
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(TabsContent, {
                                    value: "details",
                                    className: "mt-0 flex-1",
                                    children: /*#__PURE__*/ _jsx(ScrollArea, {
                                        className: "h-full",
                                        children: /*#__PURE__*/ _jsxs("div", {
                                            className: "space-y-4 px-6 py-4",
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            className: "flex items-center gap-2 text-sm text-muted-foreground mb-1",
                                                            children: [
                                                                /*#__PURE__*/ _jsx(Building2, {
                                                                    className: "h-4 w-4"
                                                                }),
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    className: "font-medium",
                                                                    children: "Organization"
                                                                })
                                                            ]
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "text-sm ml-6",
                                                            children: opportunity.organization.name
                                                        }),
                                                        opportunity.fullAgencyHierarchy && /*#__PURE__*/ _jsx("p", {
                                                            className: "text-xs text-muted-foreground ml-6 mt-1",
                                                            children: opportunity.fullAgencyHierarchy
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx(Separator, {}),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "grid grid-cols-2 gap-4",
                                                    children: [
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            children: [
                                                                /*#__PURE__*/ _jsxs("div", {
                                                                    className: "flex items-center gap-2 text-sm text-muted-foreground mb-1",
                                                                    children: [
                                                                        /*#__PURE__*/ _jsx(Calendar, {
                                                                            className: "h-4 w-4"
                                                                        }),
                                                                        /*#__PURE__*/ _jsx("span", {
                                                                            className: "font-medium",
                                                                            children: "Posted Date"
                                                                        })
                                                                    ]
                                                                }),
                                                                /*#__PURE__*/ _jsx("p", {
                                                                    className: "text-sm ml-6",
                                                                    children: formatDate(opportunity.postedDate)
                                                                })
                                                            ]
                                                        }),
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            children: [
                                                                /*#__PURE__*/ _jsxs("div", {
                                                                    className: "flex items-center gap-2 text-sm text-muted-foreground mb-1",
                                                                    children: [
                                                                        /*#__PURE__*/ _jsx(Clock, {
                                                                            className: "h-4 w-4"
                                                                        }),
                                                                        /*#__PURE__*/ _jsx("span", {
                                                                            className: "font-medium",
                                                                            children: "Response Deadline"
                                                                        })
                                                                    ]
                                                                }),
                                                                /*#__PURE__*/ _jsx("p", {
                                                                    className: "text-sm ml-6",
                                                                    children: opportunity.responseDeadLine ? formatDate(opportunity.responseDeadLine) : 'Not specified'
                                                                }),
                                                                opportunity.responseDeadLine && /*#__PURE__*/ _jsx("p", {
                                                                    className: "text-xs text-muted-foreground ml-6 mt-1",
                                                                    children: daysUntil
                                                                })
                                                            ]
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx(Separator, {}),
                                                opportunity.naicsCodes.length > 0 && /*#__PURE__*/ _jsxs(_Fragment, {
                                                    children: [
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            children: [
                                                                /*#__PURE__*/ _jsxs("div", {
                                                                    className: "flex items-center gap-2 text-sm text-muted-foreground mb-2",
                                                                    children: [
                                                                        /*#__PURE__*/ _jsx(Tag, {
                                                                            className: "h-4 w-4"
                                                                        }),
                                                                        /*#__PURE__*/ _jsx("span", {
                                                                            className: "font-medium",
                                                                            children: "NAICS Codes"
                                                                        })
                                                                    ]
                                                                }),
                                                                /*#__PURE__*/ _jsx("div", {
                                                                    className: "flex flex-wrap gap-2 ml-6",
                                                                    children: opportunity.naicsCodes.map((code)=>/*#__PURE__*/ _jsx(Badge, {
                                                                            variant: "secondary",
                                                                            className: "font-mono",
                                                                            children: code
                                                                        }, code))
                                                                })
                                                            ]
                                                        }),
                                                        /*#__PURE__*/ _jsx(Separator, {})
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            className: "flex items-center gap-2 text-sm text-muted-foreground mb-1",
                                                            children: [
                                                                /*#__PURE__*/ _jsx(FileText, {
                                                                    className: "h-4 w-4"
                                                                }),
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    className: "font-medium",
                                                                    children: "Notice ID"
                                                                })
                                                            ]
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "text-sm ml-6 font-mono",
                                                            "data-testid": Testids.opportunities.detailNoticeId,
                                                            children: opportunity.noticeId
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx(Separator, {}),
                                                /*#__PURE__*/ _jsx(NoticeCoverage, {
                                                    noticeId: opportunity.noticeId
                                                }),
                                                /*#__PURE__*/ _jsx(LinkedOpportunities, {
                                                    noticeId: opportunity.noticeId
                                                }),
                                                /*#__PURE__*/ _jsx(NoticeQualificationEntries, {
                                                    noticeId: opportunity.noticeId
                                                }),
                                                /*#__PURE__*/ _jsx(OpportunityWatch, {
                                                    noticeId: opportunity.noticeId,
                                                    onNavigate: onNavigate
                                                }),
                                                /*#__PURE__*/ _jsx(NoticePublications, {
                                                    noticeId: opportunity.noticeId
                                                })
                                            ]
                                        })
                                    })
                                }),
                                /*#__PURE__*/ _jsx(TabsContent, {
                                    value: "description",
                                    className: "mt-0 flex-1",
                                    children: /*#__PURE__*/ _jsxs("div", {
                                        className: "flex flex-col px-6 py-4 gap-4",
                                        children: [
                                            opportunity.summary && /*#__PURE__*/ _jsx("div", {
                                                className: "pb-4 border-b shrink-0",
                                                children: /*#__PURE__*/ _jsxs("div", {
                                                    className: "flex items-start gap-2",
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Sparkles, {
                                                            className: "h-4 w-4 text-primary shrink-0 mt-0.5"
                                                        }),
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            className: "flex-1",
                                                            children: [
                                                                /*#__PURE__*/ _jsx("p", {
                                                                    className: "text-xs font-medium text-muted-foreground mb-2",
                                                                    children: "AI Summary"
                                                                }),
                                                                /*#__PURE__*/ _jsx("p", {
                                                                    className: "text-sm leading-relaxed",
                                                                    children: opportunity.summary
                                                                })
                                                            ]
                                                        })
                                                    ]
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(ScrollArea, {
                                                className: "flex-1 pr-1",
                                                children: /*#__PURE__*/ _jsx("div", {
                                                    className: "prose prose-sm max-w-none dark:prose-invert",
                                                    children: opportunity.description ? // biome-ignore lint/security/noDangerouslySetInnerHtml: Content is sanitized with DOMPurify
                                                    /*#__PURE__*/ _jsx("div", {
                                                        dangerouslySetInnerHTML: {
                                                            __html: sanitizedDescription
                                                        }
                                                    }) : opportunity.descriptionLink ? /*#__PURE__*/ _jsxs("div", {
                                                        className: "text-sm text-muted-foreground",
                                                        children: [
                                                            /*#__PURE__*/ _jsx("p", {
                                                                className: "mb-2",
                                                                children: "Full description not available. View it directly on SAM.gov:"
                                                            }),
                                                            /*#__PURE__*/ _jsxs("a", {
                                                                href: opportunity.descriptionLink,
                                                                target: "_blank",
                                                                rel: "noopener noreferrer",
                                                                className: "text-primary hover:underline inline-flex items-center gap-1",
                                                                children: [
                                                                    "View Full Description",
                                                                    /*#__PURE__*/ _jsx(ExternalLink, {
                                                                        className: "h-3 w-3"
                                                                    })
                                                                ]
                                                            })
                                                        ]
                                                    }) : /*#__PURE__*/ _jsx("p", {
                                                        className: "text-sm text-muted-foreground",
                                                        children: "No description available."
                                                    })
                                                })
                                            })
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsx(TabsContent, {
                                    value: "similar",
                                    className: "mt-0 flex-1",
                                    children: /*#__PURE__*/ _jsx(ScrollArea, {
                                        className: "h-full",
                                        children: /*#__PURE__*/ _jsxs("div", {
                                            className: "px-6 py-4 space-y-3",
                                            children: [
                                                similarLoading && /*#__PURE__*/ _jsx("div", {
                                                    className: "space-y-3",
                                                    children: Array.from({
                                                        length: 3
                                                    }).map((_, i)=>/*#__PURE__*/ _jsxs("div", {
                                                            className: "rounded-lg border p-4 animate-pulse",
                                                            children: [
                                                                /*#__PURE__*/ _jsx("div", {
                                                                    className: "h-4 bg-muted rounded w-3/4 mb-2"
                                                                }),
                                                                /*#__PURE__*/ _jsx("div", {
                                                                    className: "h-3 bg-muted rounded w-1/2 mb-3"
                                                                }),
                                                                /*#__PURE__*/ _jsx("div", {
                                                                    className: "h-3 bg-muted rounded w-full"
                                                                })
                                                            ]
                                                        }, i))
                                                }),
                                                similarError && /*#__PURE__*/ _jsx("p", {
                                                    className: "text-sm text-muted-foreground py-8 text-center",
                                                    children: "Could not load similar opportunities."
                                                }),
                                                !similarLoading && !similarError && similarData?.opportunities.length === 0 && /*#__PURE__*/ _jsx("p", {
                                                    className: "text-sm text-muted-foreground py-8 text-center",
                                                    children: "No similar opportunities found."
                                                }),
                                                !similarLoading && similarData?.opportunities.map((similar)=>{
                                                    const deadlineDays = similar.responseDeadLine ? Math.ceil((new Date(similar.responseDeadLine).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null;
                                                    const isExpired = deadlineDays !== null && deadlineDays < 0;
                                                    const isUrgentSimilar = deadlineDays !== null && deadlineDays <= 7 && deadlineDays > 0;
                                                    const scorePercent = Math.round(similar.score * 100);
                                                    return /*#__PURE__*/ _jsxs("div", {
                                                        className: "rounded-lg border hover:shadow-md transition-shadow hover:border-primary/50 group",
                                                        children: [
                                                            /*#__PURE__*/ _jsxs("button", {
                                                                type: "button",
                                                                className: "block w-full text-left p-4 pb-2",
                                                                onClick: ()=>onNavigate(similar.noticeId),
                                                                children: [
                                                                    /*#__PURE__*/ _jsxs("div", {
                                                                        className: "flex items-start justify-between gap-3 mb-2",
                                                                        children: [
                                                                            /*#__PURE__*/ _jsx("h4", {
                                                                                className: "text-sm font-medium leading-snug line-clamp-2 group-hover:text-primary transition-colors flex-1",
                                                                                children: similar.title
                                                                            }),
                                                                            /*#__PURE__*/ _jsxs("span", {
                                                                                className: "shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary/10 text-primary tabular-nums",
                                                                                title: "Relevance score",
                                                                                children: [
                                                                                    scorePercent,
                                                                                    "%"
                                                                                ]
                                                                            })
                                                                        ]
                                                                    }),
                                                                    /*#__PURE__*/ _jsxs("div", {
                                                                        className: "flex items-center gap-1 text-xs text-muted-foreground mb-2",
                                                                        children: [
                                                                            /*#__PURE__*/ _jsx(Building2, {
                                                                                className: "h-3 w-3 shrink-0"
                                                                            }),
                                                                            /*#__PURE__*/ _jsx("span", {
                                                                                className: "line-clamp-1",
                                                                                children: similar.organization.name
                                                                            })
                                                                        ]
                                                                    }),
                                                                    similar.summary && /*#__PURE__*/ _jsx("p", {
                                                                        className: "text-xs text-muted-foreground line-clamp-2 mb-2",
                                                                        children: similar.summary
                                                                    }),
                                                                    similar.timeline && similar.timeline.length > 1 && /*#__PURE__*/ _jsxs("div", {
                                                                        className: "mb-3 pb-2 border-t pt-2",
                                                                        children: [
                                                                            /*#__PURE__*/ _jsxs("p", {
                                                                                className: "text-[10px] font-medium text-muted-foreground mb-1.5",
                                                                                children: [
                                                                                    "Amendment History (",
                                                                                    similar.timeline.length,
                                                                                    " versions)"
                                                                                ]
                                                                            }),
                                                                            /*#__PURE__*/ _jsxs("div", {
                                                                                className: "space-y-0.5",
                                                                                children: [
                                                                                    similar.timeline.slice(0, 3).map((entry, idx)=>/*#__PURE__*/ _jsxs("div", {
                                                                                            className: "text-[10px] text-muted-foreground flex items-center gap-1.5",
                                                                                            children: [
                                                                                                /*#__PURE__*/ _jsx("span", {
                                                                                                    className: "font-mono font-medium",
                                                                                                    children: idx === 0 ? '●' : '○'
                                                                                                }),
                                                                                                /*#__PURE__*/ _jsxs("span", {
                                                                                                    children: [
                                                                                                        "Amendment ",
                                                                                                        entry.amendmentCount
                                                                                                    ]
                                                                                                }),
                                                                                                /*#__PURE__*/ _jsx("span", {
                                                                                                    className: "text-muted-foreground/60",
                                                                                                    children: "•"
                                                                                                }),
                                                                                                /*#__PURE__*/ _jsx("span", {
                                                                                                    children: new Date(entry.postedDate).toLocaleDateString('en-US', {
                                                                                                        month: 'short',
                                                                                                        day: 'numeric',
                                                                                                        year: 'numeric'
                                                                                                    })
                                                                                                })
                                                                                            ]
                                                                                        }, entry.noticeId)),
                                                                                    similar.timeline.length > 3 && /*#__PURE__*/ _jsxs("p", {
                                                                                        className: "text-[10px] text-muted-foreground/60 pl-3.5",
                                                                                        children: [
                                                                                            "+ ",
                                                                                            similar.timeline.length - 3,
                                                                                            " more"
                                                                                        ]
                                                                                    })
                                                                                ]
                                                                            })
                                                                        ]
                                                                    }),
                                                                    /*#__PURE__*/ _jsxs("div", {
                                                                        className: "flex items-center flex-wrap gap-2",
                                                                        children: [
                                                                            similar.typeOfSetAsideDescription && /*#__PURE__*/ _jsx(Badge, {
                                                                                variant: "outline",
                                                                                className: "text-[10px] px-1.5 py-0",
                                                                                children: similar.typeOfSetAsideDescription
                                                                            }),
                                                                            similar.naicsCodes.slice(0, 2).map((code)=>/*#__PURE__*/ _jsx(Badge, {
                                                                                    variant: "secondary",
                                                                                    className: "text-[10px] font-mono px-1.5 py-0",
                                                                                    children: code
                                                                                }, code)),
                                                                            similar.responseDeadLine && /*#__PURE__*/ _jsxs(Badge, {
                                                                                variant: isExpired ? 'outline' : isUrgentSimilar ? 'destructive' : 'secondary',
                                                                                className: "text-[10px] px-1.5 py-0 shrink-0",
                                                                                children: [
                                                                                    /*#__PURE__*/ _jsx(Clock, {
                                                                                        className: "h-2.5 w-2.5 mr-1"
                                                                                    }),
                                                                                    isExpired ? 'Expired' : deadlineDays === 0 ? 'Due today' : `${deadlineDays}d left`
                                                                                ]
                                                                            })
                                                                        ]
                                                                    })
                                                                ]
                                                            }),
                                                            /*#__PURE__*/ _jsx("div", {
                                                                className: "px-4 pb-3",
                                                                children: /*#__PURE__*/ _jsxs("a", {
                                                                    href: similar.uiLink,
                                                                    target: "_blank",
                                                                    rel: "noopener noreferrer",
                                                                    className: "inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary transition-colors",
                                                                    onClick: (e)=>e.stopPropagation(),
                                                                    children: [
                                                                        /*#__PURE__*/ _jsx(ExternalLink, {
                                                                            className: "h-2.5 w-2.5"
                                                                        }),
                                                                        "View on SAM.gov"
                                                                    ]
                                                                })
                                                            })
                                                        ]
                                                    }, similar.noticeId);
                                                })
                                            ]
                                        })
                                    })
                                }),
                                /*#__PURE__*/ _jsx(TabsContent, {
                                    value: "history",
                                    className: "mt-0 flex-1",
                                    children: /*#__PURE__*/ _jsx(ScrollArea, {
                                        className: "h-full",
                                        children: /*#__PURE__*/ _jsxs("div", {
                                            className: "px-6 py-4",
                                            children: [
                                                historyLoading && /*#__PURE__*/ _jsx("div", {
                                                    className: "space-y-4",
                                                    children: Array.from({
                                                        length: 3
                                                    }).map((_, i)=>/*#__PURE__*/ _jsxs("div", {
                                                            className: "flex gap-3 animate-pulse",
                                                            children: [
                                                                /*#__PURE__*/ _jsxs("div", {
                                                                    className: "flex flex-col items-center gap-1 shrink-0",
                                                                    children: [
                                                                        /*#__PURE__*/ _jsx("div", {
                                                                            className: "h-6 w-6 rounded-full bg-muted"
                                                                        }),
                                                                        /*#__PURE__*/ _jsx("div", {
                                                                            className: "w-px flex-1 bg-muted min-h-[40px]"
                                                                        })
                                                                    ]
                                                                }),
                                                                /*#__PURE__*/ _jsxs("div", {
                                                                    className: "flex-1 pb-6",
                                                                    children: [
                                                                        /*#__PURE__*/ _jsx("div", {
                                                                            className: "h-3 bg-muted rounded w-1/3 mb-2"
                                                                        }),
                                                                        /*#__PURE__*/ _jsx("div", {
                                                                            className: "h-4 bg-muted rounded w-2/3"
                                                                        })
                                                                    ]
                                                                })
                                                            ]
                                                        }, i))
                                                }),
                                                historyError && /*#__PURE__*/ _jsx("p", {
                                                    className: "text-sm text-muted-foreground py-8 text-center",
                                                    children: "Could not load amendment history."
                                                }),
                                                !historyLoading && !historyError && historyData?.entries.length === 1 && historyData.entries[0]?.isCurrent && /*#__PURE__*/ _jsx("p", {
                                                    className: "text-sm text-muted-foreground py-8 text-center",
                                                    children: "No amendments on record — this is the original posting."
                                                }),
                                                !historyLoading && !historyError && historyData && historyData.entries.length > 0 && !(historyData.entries.length === 1 && historyData.entries[0]?.isCurrent) && /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        historyData.solicitationNumber && /*#__PURE__*/ _jsxs("p", {
                                                            className: "text-xs text-muted-foreground mb-4 font-mono",
                                                            children: [
                                                                "Solicitation: ",
                                                                historyData.solicitationNumber
                                                            ]
                                                        }),
                                                        /*#__PURE__*/ _jsx("ol", {
                                                            className: "relative",
                                                            children: historyData.entries.map((entry, idx)=>{
                                                                const isLast = idx === historyData.entries.length - 1;
                                                                const label = entry.amendmentCount === 0 ? 'Original' : `Amendment #${entry.amendmentCount}`;
                                                                const dateStr = entry.modifiedDate ?? entry.postedDate;
                                                                return /*#__PURE__*/ _jsxs("li", {
                                                                    className: "flex gap-3",
                                                                    children: [
                                                                        /*#__PURE__*/ _jsxs("div", {
                                                                            className: "flex flex-col items-center shrink-0",
                                                                            children: [
                                                                                /*#__PURE__*/ _jsx("div", {
                                                                                    className: `h-6 w-6 rounded-full border-2 flex items-center justify-center shrink-0 ${entry.isCurrent ? 'border-primary bg-primary' : 'border-muted-foreground/40 bg-background'}`,
                                                                                    children: /*#__PURE__*/ _jsx(GitBranch, {
                                                                                        className: `h-3 w-3 ${entry.isCurrent ? 'text-primary-foreground' : 'text-muted-foreground'}`
                                                                                    })
                                                                                }),
                                                                                !isLast && /*#__PURE__*/ _jsx("div", {
                                                                                    className: "w-px flex-1 bg-border mt-1"
                                                                                })
                                                                            ]
                                                                        }),
                                                                        /*#__PURE__*/ _jsxs("div", {
                                                                            className: `flex-1 pb-6 ${isLast ? 'pb-2' : ''}`,
                                                                            children: [
                                                                                /*#__PURE__*/ _jsxs("div", {
                                                                                    className: "flex items-center gap-2 mb-1 flex-wrap",
                                                                                    children: [
                                                                                        /*#__PURE__*/ _jsx("span", {
                                                                                            className: `text-xs font-semibold ${entry.isCurrent ? 'text-primary' : 'text-muted-foreground'}`,
                                                                                            children: label
                                                                                        }),
                                                                                        entry.isCurrent && /*#__PURE__*/ _jsx(Badge, {
                                                                                            variant: "outline",
                                                                                            className: "text-[10px] px-1.5 py-0 border-primary text-primary",
                                                                                            children: "Current"
                                                                                        }),
                                                                                        /*#__PURE__*/ _jsx("span", {
                                                                                            className: "text-xs text-muted-foreground",
                                                                                            children: formatDate(dateStr)
                                                                                        })
                                                                                    ]
                                                                                }),
                                                                                /*#__PURE__*/ _jsx("p", {
                                                                                    className: "text-sm mb-2",
                                                                                    children: entry.changes
                                                                                }),
                                                                                entry.attachments.length > 0 && /*#__PURE__*/ _jsx("div", {
                                                                                    className: "flex flex-wrap gap-1.5 mb-2",
                                                                                    children: entry.attachments.map((url, aIdx)=>{
                                                                                        const name = url.split('/').pop() ?? `Attachment ${aIdx + 1}`;
                                                                                        return /*#__PURE__*/ _jsxs("a", {
                                                                                            href: url,
                                                                                            target: "_blank",
                                                                                            rel: "noopener noreferrer",
                                                                                            className: "inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border hover:border-primary hover:text-primary transition-colors",
                                                                                            children: [
                                                                                                /*#__PURE__*/ _jsx(FileText, {
                                                                                                    className: "h-2.5 w-2.5 shrink-0"
                                                                                                }),
                                                                                                /*#__PURE__*/ _jsx("span", {
                                                                                                    className: "truncate max-w-[160px]",
                                                                                                    children: decodeURIComponent(name)
                                                                                                })
                                                                                            ]
                                                                                        }, url);
                                                                                    })
                                                                                }),
                                                                                /*#__PURE__*/ _jsxs("a", {
                                                                                    href: entry.uiLink,
                                                                                    target: "_blank",
                                                                                    rel: "noopener noreferrer",
                                                                                    className: "inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors",
                                                                                    children: [
                                                                                        /*#__PURE__*/ _jsx(ExternalLink, {
                                                                                            className: "h-3 w-3"
                                                                                        }),
                                                                                        "View on SAM.gov"
                                                                                    ]
                                                                                })
                                                                            ]
                                                                        })
                                                                    ]
                                                                }, entry.noticeId);
                                                            })
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    })
                                })
                            ]
                        })
                    })
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "px-6 py-4 border-t shrink-0 flex justify-end",
                    children: /*#__PURE__*/ _jsxs(Button, {
                        onClick: handleViewOnSAM,
                        children: [
                            /*#__PURE__*/ _jsx(ExternalLink, {
                                className: "h-4 w-4 mr-2"
                            }),
                            "View on SAM.gov"
                        ]
                    })
                })
            ]
        })
    });
}
